import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import type { PARInvoiceRow } from "@/lib/par/csvInvoiceParser";
import type { EmailConfig } from "@/components/par/NotificationSettingsModal";
export type { EmailConfig };

function makeRowKey(r: {
  vendor_name: string | null;
  vendor_number: string | null;
  invoice_number: string | null;
  terms: string | null;
  invoiced_date: string | null;
  due_date: string | null;
  early_pay_due_date: string | null;
  paid_date: string | null;
  payment_status: string | null;
  amount_invoiced: number | null;
  amount_paid: number | null;
  discount_pct: number | null;
  potential_savings: number | null;
  days_paid_early: number | null;
}): string {
  return [
    r.vendor_name ?? "",
    r.vendor_number ?? "",
    r.invoice_number ?? "",
    r.terms ?? "",
    r.invoiced_date ?? "",
    r.due_date ?? "",
    r.early_pay_due_date ?? "",
    r.paid_date ?? "",
    r.payment_status ?? "",
    String(r.amount_invoiced ?? 0),
    String(r.amount_paid ?? 0),
    String(r.discount_pct ?? ""),
    String(r.potential_savings ?? 0),
    String(r.days_paid_early ?? 0),
  ].join("|");
}

export type ReminderMode = "early_only" | "all";

export interface ReminderSettings {
  id?: string;
  days_before: number;
  enabled: boolean;
  mode: ReminderMode;
  notification_email: string;
  notification_emails: string[];
  early_pay_only: boolean;
  min_days_early: number;
  summary_email: boolean;
  email_configs: EmailConfig[];
  /** Optional minimum invoice amount for notification eligibility */
  min_amount?: number;
  /** Timestamp of the last sent notification email */
  last_sent_at?: string;
}

export function usePARReminders() {
  const { session, companyId } = useAuth();
  const { toast } = useToast();
  const [settings, setSettings] = useState<ReminderSettings>({
    days_before: 3,
    enabled: false,
    mode: "early_only",
    notification_email: "",
    notification_emails: [],
    early_pay_only: false,
    min_days_early: 0,
    summary_email: false,
    email_configs: [],
    min_amount: undefined,
    last_sent_at: undefined,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const userId = session?.user?.id;

  // Load settings
  useEffect(() => {
    if (!userId) return;
    (async () => {
      const { data, error } = await supabase
        .from("par_reminder_settings")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (!error && data) {
        const raw = data as any;
        setSettings({
          id: data.id,
          days_before: data.days_before,
          enabled: data.enabled,
          mode: (raw.mode as "early_only" | "all") ?? "early_only",
          notification_email: data.notification_email,
          notification_emails: Array.isArray(raw.notification_emails)
            ? raw.notification_emails
            : [],
          early_pay_only: raw.early_pay_only ?? false,
          min_days_early: raw.min_days_early ?? 0,
          summary_email: raw.summary_email ?? false,
          email_configs: Array.isArray(raw.email_configs) ? raw.email_configs : [],
          min_amount: raw.min_amount != null ? Number(raw.min_amount) : undefined,
          last_sent_at: raw.last_sent_at ?? undefined,
        });
      } else {
        // Default email from user
        const defaultEmail = session?.user?.email || "";
        setSettings((s) => ({
          ...s,
          notification_email: defaultEmail,
          notification_emails: defaultEmail ? [defaultEmail] : [],
          email_configs: defaultEmail
            ? [
                {
                  email: defaultEmail,
                  enabled: true,
                  days_before: 3,
                  early_pay_only: false,
                  min_days_early: 0,
                  summary_email: false,
                },
              ]
            : [],
        }));
      }
      setLoading(false);
    })();
  }, [userId, session?.user?.email]);

  const saveSettings = useCallback(
    async (newSettings: Partial<ReminderSettings>) => {
      if (!userId) return;
      setSaving(true);
      const merged = { ...settings, ...newSettings };
      try {
        const payload = {
          days_before: merged.days_before,
          enabled: merged.enabled,
          mode: merged.mode ?? "early_only",
          notification_email: merged.notification_email,
          notification_emails: merged.notification_emails,
          early_pay_only: merged.early_pay_only,
          min_days_early: merged.min_days_early,
          summary_email: merged.summary_email,
          email_configs: merged.email_configs,
          min_amount: merged.min_amount ?? null,
          updated_at: new Date().toISOString(),
        };
        const { data, error } = await supabase
          .from("par_reminder_settings")
          .upsert({ user_id: userId, ...payload } as any, { onConflict: "user_id" })
          .select()
          .single();
        if (error) throw error;
        if (data?.id) merged.id = data.id;
        setSettings(merged);
        toast({ title: "Notification settings saved" });
      } catch (err: any) {
        toast({ title: "Error saving settings", description: err.message, variant: "destructive" });
      } finally {
        setSaving(false);
      }
    },
    [userId, settings, toast]
  );

  // Persist invoice rows to the database (append with dedup by vendor_name + invoice_number)
  // If a row already exists and the new CSV has paid info, update the existing row.
  const persistInvoices = useCallback(
    async (rows: PARInvoiceRow[], fileName: string, batchId?: string): Promise<string> => {
      const resolvedBatchId = batchId ?? crypto.randomUUID();
      if (!userId || rows.length === 0) return resolvedBatchId;

      // Load existing rows to deduplicate and detect updates (paginated to bypass 1000-row limit)
      let existingAll: any[] = [];
      const dedupBatchSize = 1000;
      let dedupFrom = 0;
      while (true) {
        const { data: batch } = await supabase
          .from("par_invoices")
          .select(
            "id, vendor_name, vendor_number, invoice_number, terms, invoiced_date, due_date, early_pay_due_date, paid_date, payment_status, amount_invoiced, amount_paid, discount_pct, potential_savings, days_paid_early"
          )
          .range(dedupFrom, dedupFrom + dedupBatchSize - 1);
        if (!batch || batch.length === 0) break;
        existingAll = [...existingAll, ...batch];
        if (batch.length < dedupBatchSize) break;
        dedupFrom += dedupBatchSize;
      }
      const existing = existingAll;

      // Set of full-row composite keys for exact-duplicate detection
      const existingKeySet = new Set<string>(existing.map((r) => makeRowKey(r)));

      // Map: "vendor_name|invoice_number" → existing DB row (for paid-status update logic)
      const existingMap = new Map<
        string,
        { id: string; payment_status: string; amount_paid: number }
      >(
        (existing ?? []).map((r) => [
          `${r.vendor_name}|${r.invoice_number ?? ""}`,
          {
            id: r.id,
            payment_status: r.payment_status ?? "",
            amount_paid: r.amount_paid ?? 0,
          },
        ])
      );

      const toInsert: PARInvoiceRow[] = [];
      const toUpdate: Array<{ id: string; updates: Record<string, unknown> }> = [];

      for (const r of rows) {
        const fullKey = makeRowKey({
          vendor_name: r.vendor_name,
          vendor_number: r.vendor_number || null,
          invoice_number: r.invoice_number || null,
          terms: r.terms || null,
          invoiced_date: r.invoiced_date || null,
          due_date: r.due_date || null,
          early_pay_due_date: r.early_pay_due_date || null,
          paid_date: r.paid_date || null,
          payment_status: r.payment_status || null,
          amount_invoiced: r.amount_invoiced,
          amount_paid: r.amount_paid,
          discount_pct: r.discount_pct,
          potential_savings: r.potential_savings,
          days_paid_early: r.days_paid_early,
        });

        // Exact duplicate — every content field matches an existing DB row, skip
        if (existingKeySet.has(fullKey)) {
          continue;
        }

        const vendorInvoiceKey = `${r.vendor_name}|${r.invoice_number ?? ""}`;
        const dbRow = existingMap.get(vendorInvoiceKey);

        if (dbRow) {
          // Same vendor+invoice# but different data — check if this is a paid-status update
          const newIsPaid =
            r.payment_status.toLowerCase().includes("paid") || r.amount_paid > dbRow.amount_paid;
          const existingIsPaid = dbRow.payment_status.toLowerCase().includes("paid");

          if (newIsPaid && !existingIsPaid) {
            toUpdate.push({
              id: dbRow.id,
              updates: {
                payment_status: r.payment_status || "Paid",
                amount_paid: r.amount_paid,
                paid_date: r.paid_date || null,
                days_paid_early: r.days_paid_early,
                potential_savings: r.potential_savings,
              },
            });
          } else {
            // Different data but not a paid update — insert as new record
            toInsert.push(r);
          }
        } else {
          // Completely new vendor+invoice — insert
          toInsert.push(r);
        }
      }

      // Apply updates one-by-one (small batches expected)
      for (const { id, updates } of toUpdate) {
        const { error } = await supabase
          .from("par_invoices")
          .update(updates as any)
          .eq("id", id);
        if (error) console.error("Error updating invoice:", error);
      }

      if (toInsert.length === 0 && toUpdate.length === 0) {
        toast({
          title: "No new invoices",
          description: "All rows in this file are already in the database.",
        });
        return resolvedBatchId;
      }

      // Insert new rows in chunks of 500
      const chunkSize = 500;
      for (let i = 0; i < toInsert.length; i += chunkSize) {
        const chunk = toInsert.slice(i, i + chunkSize).map((r) => ({
          user_id: userId,
          // Only include company_id when it is known; omitting it (vs. passing null)
          // means existing rows with a null company_id are still accessible via the
          // per-user RLS fallback policy rather than being silently orphaned.
          ...(companyId != null ? { company_id: companyId } : {}),
          vendor_name: r.vendor_name,
          vendor_number: r.vendor_number || null,
          invoice_number: r.invoice_number || null,
          terms: r.terms || null,
          invoiced_date: r.invoiced_date || null,
          due_date: r.due_date || null,
          early_pay_due_date: r.early_pay_due_date || null,
          paid_date: r.paid_date || null,
          payment_status: r.payment_status || "",
          amount_invoiced: r.amount_invoiced,
          amount_paid: r.amount_paid,
          discount_pct: r.discount_pct,
          potential_savings: r.potential_savings,
          days_paid_early: r.days_paid_early,
          upload_batch_id: resolvedBatchId,
          file_name: fileName,
        }));
        const { error } = await supabase.from("par_invoices").insert(chunk as any);
        if (error) {
          console.error("Error persisting invoices:", error);
          toast({
            title: "Error saving invoice data",
            description: error.message,
            variant: "destructive",
          });
          return resolvedBatchId;
        }
      }

      const parts: string[] = [];
      if (toInsert.length > 0) parts.push(`${toInsert.length} new invoices stored`);
      if (toUpdate.length > 0) parts.push(`${toUpdate.length} updated to paid`);
      toast({
        title: "Invoice data saved",
        description: parts.join(" · "),
      });
      return resolvedBatchId;
    },
    [userId, toast]
  );

  return { settings, loading, saving, saveSettings, persistInvoices };
}
